import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classes from '../Resources/ResourceCard/ResourceCardPanels/ResourceCardPanel.module.scss';
import { resourceHelpers } from '@/utils/helpers';
import { isObject } from '@/utils/utils';
import t from '@/utils/Language/ResourceCard-translations';
import upperFirst from 'lodash/upperFirst';

// Ensure first letter is capitalized for presentation in the Property panel
const displayText = (text) => (typeof text === 'string' ? upperFirst(text) : text);

const getPropertyIcon = (property) => {
  if (resourceHelpers.PROPERTY_ICONS[property]) return resourceHelpers.PROPERTY_ICONS[property];
  if (property.startsWith('cost-')) return ['fas', 'dollar-sign'];
  if (property.startsWith('ada-')) return ['fas', 'wheelchair'];
  return ['fas', 'circle-info'];
};

// Renders a property value, which the API returns in one of three shapes:
//   - array of strings, comma-joined; entries may carry a bracketed eligibility prefix
//     (e.g. "[age-elig]18-24") where only the prefix is translated and the remainder is
//     appended verbatim
//   - plain string, mapped through propertyLabel then translated
//   - object, treated as a {min, max} range
const renderPropertyValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((val, index) => {
      const separator = index === value.length - 1 ? '' : ', ';
      const specialCaseProp = ['resident-of', 'age-elig', 'grade-elig', 'child-elig', 'income-elig'].find(
        (prop) => val.indexOf('[' + prop + ']') === 0
      );
      if (specialCaseProp) {
        const remainder = val.replace('[' + specialCaseProp + ']', '');
        return <span key={index}>{displayText(t(specialCaseProp)) + remainder + separator}</span>;
      }
      return <span key={index}>{displayText(t(val)) + separator}</span>;
    });
  }
  if (typeof value === 'string') return displayText(t(resourceHelpers.propertyLabel(value)));
  if (isObject(value)) return resourceHelpers.propertyRange(value);
  return null;
};

// listedProperties is the already-filtered output of resourceHelpers.listedProperties --
// the caller computes it once and shares it across instances.
const OpportunityProperties = ({ listedProperties, includeKeys, excludeKeys, hideDividers = false, noLabelKeys = [] }) => {
  let propertyNames = Object.keys(listedProperties || {});
  if (includeKeys) {
    propertyNames = propertyNames.filter((property) => includeKeys.includes(property));
  } else if (excludeKeys) {
    propertyNames = propertyNames.filter((property) => !excludeKeys.includes(property));
  }

  if (!propertyNames.length) {
    return null;
  }

  return (
    <div className={classes.propertyList}>
      {propertyNames.map((property) => {
        const rowClasses = [
          classes.propertyRow,
          hideDividers && classes.propertyRowNoDivider,
          noLabelKeys.includes(property) && classes.propertyRowFullWidth
        ]
          .filter(Boolean)
          .join(' ');

        if (noLabelKeys.includes(property)) {
          return (
            <div key={property} className={rowClasses}>
              <div className={classes.propertyValue}>{renderPropertyValue(listedProperties[property])}</div>
            </div>
          );
        }

        return (
          <div key={property} className={rowClasses}>
            <div className={classes.propertyLabel}>
              <FontAwesomeIcon icon={getPropertyIcon(property)} className={classes.propertyIcon} />
              {displayText(t(property))}
            </div>
            <div className={classes.propertyValue}>{renderPropertyValue(listedProperties[property])}</div>
          </div>
        );
      })}
    </div>
  );
};

export default OpportunityProperties;
